import { useEffect, useState } from "react";
import { Link } from "wouter";
import { collection, limit, onSnapshot, orderBy, query, where } from "firebase/firestore";
import { paths, type PieceReview } from "@clevis/shared-types";
import { Card, CardTitle } from "@clevis/shared-ui";
import { db } from "../lib/firebase";
import { call } from "../lib/callFunction";

// Timesheets → piece work waiting for a manager's OK: parts techs logged
// beyond a job's plan, when the company asks for approval (Job settings).
// Until OK'd they don't pay; declined ones never do.

const money = (c: number) => (c / 100).toLocaleString("en-US", { style: "currency", currency: "USD" });

export function PieceReviews({ tenantId, people }: { tenantId: string; people?: (uid: string) => boolean }) {
  const [rows, setRows] = useState<Array<PieceReview & { id: string }>>([]);
  const [busy, setBusy] = useState<string | null>(null);
  useEffect(
    () =>
      onSnapshot(
        query(collection(db, paths.tenantPieceReviews(tenantId)), where("status", "==", "pending"), orderBy("at", "desc"), limit(100)),
        (snap) => setRows(snap.docs.map((d) => ({ id: d.id, ...(d.data() as PieceReview) }))),
        () => undefined,
      ),
    [tenantId],
  );
  const shown = people ? rows.filter((r) => people(r.techUid)) : rows;
  if (shown.length === 0) return null;
  async function review(r: PieceReview & { id: string }, approved: boolean) {
    setBusy(r.id);
    try {
      await call("reviewPieceWork", { tenantId, jobId: r.jobId, workItemId: r.workItemId, approved });
    } finally {
      setBusy(null);
    }
  }
  return (
    <Card className="p-0">
      <CardTitle className="px-4 pt-3">Piece work waiting for your OK ({shown.length})</CardTitle>
      {shown.map((r) => (
        <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 border-b border-steel/10 px-4 py-1.5 text-sm last:border-0">
          <span className="min-w-0">
            <span className="text-ink">
              {r.techName || "Tech"}: {r.quantity} × {r.description}
            </span>
            <span className="block truncate text-[11px] text-steel">
              <Link href={`/jobs/${r.jobId}`} className="hover:underline">
                {r.jobTitle || "Open the job"}
              </Link>{" "}
              · {new Date(r.at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
            </span>
          </span>
          <span className="flex shrink-0 items-center gap-3 text-xs">
            <span className="font-semibold text-ink">{money(r.amountCents)}</span>
            <button className="font-semibold text-success hover:underline" disabled={busy === r.id} onClick={() => void review(r, true)}>
              OK
            </button>
            <button className="text-steel hover:text-danger hover:underline" disabled={busy === r.id} onClick={() => void review(r, false)}>
              Decline
            </button>
          </span>
        </div>
      ))}
    </Card>
  );
}
